import { chat } from "@/lib/http";
import type { Conversation, ConversationAdmin, ConversationMember, EntityPermissions } from "./types";

export interface IncomingLink {
  id: string;
  conversation_id: string;
  name: string;
  sender_mode: string;
  bot_id: string | null;
  status: string;
  created_by: string;
  created_at: string | null;
  last_used_at: string | null;
  url: string | null;
  token?: string;
}

export function listConversations(accountId?: string) {
  const params = accountId ? `?account_id=${accountId}` : "";
  return chat<Conversation[]>(`/v1/chat/conversations${params}`);
}

export function getConversation(id: string) {
  return chat<Conversation>(`/v1/chat/conversations/${id}`);
}

export function createConversation(data: {
  type: string;
  title?: string;
  member_ids?: string[];
  account_id?: string;
  space_id?: string | null;
  access_mode?: "standalone" | "inherited" | "custom";
  visibility?: "public" | "restricted";
  is_public?: boolean;
}) {
  return chat<Conversation>("/v1/chat/conversations", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function getOrCreateDirect(targetUserId: string, accountId: string) {
  return createConversation({
    type: "direct",
    member_ids: [targetUserId],
    account_id: accountId,
  });
}

export function deleteConversation(id: string) {
  return chat<void>(`/v1/chat/conversations/${id}`, { method: "DELETE" });
}

export function updateConversation(
  id: string,
  data: {
    title?: string;
    avatar_url?: string | null;
    space_id?: string | null;
    access_mode?: "standalone" | "inherited" | "custom";
    visibility?: "public" | "restricted";
    is_public?: boolean;
    permissions?: EntityPermissions | null;
    join_greeting_enabled?: boolean;
    join_greeting_message?: string | null;
  },
) {
  return chat<Conversation>(`/v1/chat/conversations/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export function listConversationMembers(conversationId: string) {
  return chat<ConversationMember[]>(`/v1/chat/conversations/${conversationId}/members`);
}

export function getConversationBotCommands(conversationId: string) {
  return chat<Array<{ command: string; description: string }>>(
    `/v1/chat/conversations/${conversationId}/bot-commands`,
  );
}

export function addConversationMembers(conversationId: string, memberIds: string[]) {
  return chat<ConversationMember[]>(`/v1/chat/conversations/${conversationId}/members`, {
    method: "POST",
    body: JSON.stringify({ member_ids: memberIds }),
  });
}

export function updateConversationMemberRole(
  conversationId: string,
  userId: string,
  role: string,
) {
  return chat<ConversationMember>(`/v1/chat/conversations/${conversationId}/members/${userId}`, {
    method: "PATCH",
    body: JSON.stringify({ role }),
  });
}

export function removeConversationMember(conversationId: string, userId: string) {
  return chat<void>(`/v1/chat/conversations/${conversationId}/members/${userId}`, {
    method: "DELETE",
  });
}

export function listConversationAdmins(conversationId: string) {
  return chat<ConversationAdmin[]>(`/v1/chat/conversations/${conversationId}/admins`);
}

export function addConversationAdmin(
  conversationId: string,
  userId: string,
  permissions?: Record<string, unknown> | null,
) {
  return chat<ConversationAdmin>(`/v1/chat/conversations/${conversationId}/admins`, {
    method: "POST",
    body: JSON.stringify({ user_id: userId, permissions: permissions ?? null }),
  });
}

export function removeConversationAdmin(conversationId: string, userId: string) {
  return chat<void>(`/v1/chat/conversations/${conversationId}/admins/${userId}`, {
    method: "DELETE",
  });
}

export function updateConversationAdmin(
  conversationId: string,
  userId: string,
  permissions?: Record<string, unknown> | null,
) {
  return chat<ConversationAdmin>(`/v1/chat/conversations/${conversationId}/admins/${userId}`, {
    method: "PATCH",
    body: JSON.stringify({ permissions: permissions ?? null }),
  });
}

export function markConversationRead(conversationId: string, lastReadMessageId: string) {
  return chat<ConversationMember>(`/v1/chat/conversations/${conversationId}/me`, {
    method: "PATCH",
    body: JSON.stringify({ last_read_message_id: lastReadMessageId }),
  });
}

export function updateMyConversationMembership(
  conversationId: string,
  data: {
    notifications?: string;
    is_pinned?: boolean;
    is_muted?: boolean;
    is_archived?: boolean;
    last_read_message_id?: string;
  },
) {
  return chat<ConversationMember>(`/v1/chat/conversations/${conversationId}/me`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export function sendTypingIndicator(conversationId: string): Promise<void> {
  return chat<void>(`/v1/chat/conversations/${conversationId}/typing`, {
    method: "POST",
  });
}

export function launchConversationMiniApp(conversationId: string) {
  return chat<{
    ok: true;
    result: {
      launch_url: string;
      host_url: string;
      init_data: string;
      init_data_unsafe: Record<string, unknown>;
      expires_at: string;
      display_mode?: string;
    };
  }>(`/v1/chat/conversations/${conversationId}/mini-app/launch`, {
    method: "POST",
  });
}

export function listConversationIncomingLinks(conversationId: string) {
  return chat<IncomingLink[]>(`/v1/chat/conversations/${conversationId}/incoming-links`);
}

export function createConversationIncomingLink(
  conversationId: string,
  data: { name: string; sender_mode: "system" | "bot"; bot_id?: string | null },
) {
  return chat<IncomingLink>(`/v1/chat/conversations/${conversationId}/incoming-links`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function revokeConversationIncomingLink(conversationId: string, linkId: string) {
  return chat<void>(`/v1/chat/conversations/${conversationId}/incoming-links/${linkId}`, {
    method: "DELETE",
  });
}
